import { GoogleGenerativeAI } from '@google/generative-ai';

const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
console.log('API Key 존재:', !!apiKey);
console.log('ENV 변수:', import.meta.env.VITE_GEMINI_API_KEY);

if (!apiKey) {
  console.error('VITE_GEMINI_API_KEY가 설정되지 않았습니다.');
}

const genAI = new GoogleGenerativeAI(apiKey);

export async function generateBusinessGuide(businessData) {
  // 여러 모델 시도 순서 (2.0 Flash → 1.5 Flash → 1.5 Pro)
  const modelNames = [
    'gemini-2.0-flash-exp',
    'gemini-1.5-flash-latest',
    'gemini-1.5-flash',
    'gemini-1.5-pro-latest',
    'gemini-1.5-pro'
  ];

  for (const modelName of modelNames) {
    try {
      console.log(`${modelName}으로 비즈니스 가이드 생성 시도:`, businessData);
      const model = genAI.getGenerativeModel({ model: modelName });

      const prompt = `당신은 쿠팡, 무신사, 마켓컬리, 토스, 배달의민족 등 한국 스타트업 성공 사례를 직접 컨설팅한 20년 경력의 비즈니스 실무 전문가입니다.

**고객 개별 맞춤 정보:**
- 업종: ${businessData.industry || '미지정'}
- 현재 단계: ${businessData.stage || '미지정'}
- 사업 지역: ${businessData.location || '미지정'}
- 타겟 고객: ${businessData.target || '미지정'}
- 초기 예산: ${businessData.budget || '미지정'}
- 현재 상황: ${businessData.current_situation || '미지정'}
- 보유 강점: ${businessData.unique_advantage || '미지정'}
- 구체적 목표: ${businessData.specific_goal || '미지정'}
- 희망 기간: ${businessData.timeline || '미지정'}
- 경쟁 상황: ${businessData.competition || '미지정'}
- 핵심 고민: ${businessData.concerns || '미지정'}

다음 구조로 가이드를 작성해주세요:

## 🎯 비즈니스 개요
- 사업 기회 분석
- 시장 상황 요약

## 📊 시장 분석
- 타겟 시장 규모
- 경쟁 현황
- 시장 트렌드

## 💡 핵심 전략
- 차별화 포인트
- 마케팅 전략
- 수익 모델

## 🚀 실행 계획 (30일)
- 1주차: 즉시 실행 항목
- 2주차: 기반 구축
- 3주차: 마케팅 시작
- 4주차: 최적화 및 확장

## 💰 예산 계획
- 초기 투자 항목
- 월간 운영비
- 예상 수익

## ⚠️ 주의사항
- 리스크 요소
- 대응 방안

**필수 작성 규칙:**
1. 모든 금액은 정확한 숫자로 (예: 월매출 2,850만원, 객단가 48,500원)
2. 실제 한국 업체명 사용 (예: 쿠팡, 네이버, 카카오, 무신사, 지그재그)
3. 구체적 일정과 시간 (예: 월요일 오전 9시-11시)
4. 실제 도구와 가격 명시 (예: 카페24 월 33,000원, 캔바 프로 월 14,300원)
5. "${businessData.location || '해당 지역'}" 특화 전략 포함
6. "${businessData.unique_advantage || '보유 강점'}" 최대 활용 방안
7. "${businessData.concerns || '핵심 고민'}" 해결에 중점

각 섹션은 바로 실행 가능한 구체적 액션 아이템 중심으로 작성해주세요.`;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();
      console.log(`${modelName}으로 가이드 생성 성공! 길이:`, text.length);
      return text;

    } catch (modelError) {
      console.warn(`${modelName} 모델 실패:`, modelError.message);

      // 503 에러나 모델 unavailable 에러인 경우 다음 모델 시도
      if (modelError.message?.includes('503') ||
          modelError.message?.includes('unavailable') ||
          modelError.message?.includes('not found')) {
        continue; // 다음 모델로 시도
      }

      // API key, quota, safety 에러는 바로 throw
      if (modelError.message?.includes('API key')) {
        throw new Error('API 키가 유효하지 않습니다. 관리자에게 문의하세요.');
      } else if (modelError.message?.includes('quota')) {
        throw new Error('API 사용량을 초과했습니다. 잠시 후 다시 시도해주세요.');
      } else if (modelError.message?.includes('safety')) {
        throw new Error('콘텐츠 안전 정책으로 인해 생성할 수 없습니다. 다른 내용으로 시도해주세요.');
      }

      // 다른 에러도 다음 모델로 시도
      continue;
    }
  }

  // 모든 모델이 실패한 경우
  throw new Error('모든 Gemini 모델이 현재 사용 불가능합니다. 잠시 후 다시 시도하거나 OpenAI 모델을 사용해주세요.');
}

export async function analyzeMarket(industry) {
  // 여러 모델 시도 순서
  const modelNames = [
    'gemini-2.0-flash-exp',
    'gemini-1.5-flash-latest',
    'gemini-1.5-flash',
    'gemini-1.5-pro-latest',
    'gemini-1.5-pro'
  ];

  for (const modelName of modelNames) {
    try {
      const model = genAI.getGenerativeModel({ model: modelName });

      const prompt = `${industry} 업종의 시장 분석을 해주세요.

다음 정보를 포함해주세요:
- 시장 규모 및 성장률
- 주요 트렌드
- 경쟁사 현황
- 기회 요소
- 위험 요소

간결하고 실용적인 정보로 작성해주세요.`;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      console.log(`${modelName}으로 시장 분석 성공!`);
      return response.text();

    } catch (modelError) {
      console.warn(`${modelName} 모델 실패:`, modelError.message);

      // 503 에러나 모델 unavailable 에러인 경우 다음 모델 시도
      if (modelError.message?.includes('503') ||
          modelError.message?.includes('unavailable') ||
          modelError.message?.includes('not found')) {
        continue;
      }

      // 다른 에러도 다음 모델로 시도
      continue;
    }
  }

  // 모든 모델이 실패한 경우
  throw new Error('시장 분석에 실패했습니다. 모든 Gemini 모델이 현재 사용 불가능합니다.');
}